import React, { useCallback, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import classNames from 'classnames';

import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';
import { SearchInput } from '@/shared/ui/SearchInput';

import styles from './ExperimentAssetTabShell.module.css';

export type ExperimentAssetTabShellRenderArgs = {
  searchQuery: string;
};

export type ExperimentAssetTabShellVariant = 'panel' | 'sidebar';

type ExperimentAssetTabShellProps = {
  title?: React.ReactNode;
  children: (args: ExperimentAssetTabShellRenderArgs) => React.ReactNode;
  variant?: ExperimentAssetTabShellVariant;
  onDownload?: () => void;
  downloadDisabled?: boolean;
  downloadButtonText?: string;
  downloadButtonLoading?: boolean;
  allowVerticalScroll?: boolean;
};

export const ExperimentAssetTabShell: React.FC<ExperimentAssetTabShellProps> = ({
  title,
  children,
  variant = 'panel',
  onDownload,
  downloadDisabled = false,
  downloadButtonText = '다운로드',
  downloadButtonLoading = false,
  allowVerticalScroll = false,
}) => {
  const isSidebar = variant === 'sidebar';
  const [searchQuery, setSearchQuery] = useState('');

  const handleDownload = useCallback(() => {
    onDownload?.();
  }, [onDownload]);

  return (
    <div className={classNames(styles.root, isSidebar && styles.rootSidebar)}>
      {title && (
        <div className={styles.titleRow}>
          <div className={styles.titleSlot}>{title}</div>
        </div>
      )}
      <div className={styles.searchRow}>
        <SearchInput
          className={styles.searchInput}
          placeholder="검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          aria-label="검색"
        />
        {onDownload && (
          <Button
            type="button"
            variant="outlined"
            className={styles.downloadButton}
            prefix={
              downloadButtonLoading ? (
                <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
              ) : (
                <Icon name="download" size={16} />
              )
            }
            onClick={handleDownload}
            disabled={downloadDisabled}>
            {downloadButtonText}
          </Button>
        )}
      </div>
      <div
        className={classNames(
          styles.contentCard,
          isSidebar && styles.contentPlain,
          allowVerticalScroll && styles.contentCardWithScroll
        )}>
        {children({ searchQuery })}
      </div>
    </div>
  );
};
